// ============================================================================
// L'ADRESSE D'UN ESPACE. UNE SEULE FONCTION, POUR TOUTE L'APPLICATION.
// ----------------------------------------------------------------------------
//
//        *** N'ECRIVEZ JAMAIS UNE ADRESSE D'ESPACE A LA MAIN. ***
//        *** APPELEZ adresseEspace(). C'EST LA SEULE VERITE.  ***
//
// Un espace de travail = un sous-domaine. Decision posee dans
// mockups/connexion-espaces.html le 02/08/2026, validee a l'oral, puis rappelee
// une seconde fois par le dirigeant. Elle ne se rediscute pas.
//
//   Vyndra (back-office)   app.vyndra.fr
//   Portail salarie        portail.vyndra.fr
//   Un univers metier      <son sous-domaine>.vyndra.fr
//
// Avant ce fichier, la navigation fabriquait « /hub/<slug> » a cinq endroits
// differents. Les sous-domaines etaient EN LIGNE avec leurs certificats, le
// middleware les resolvait, et absolument rien n'y envoyait personne.
//
// ----------------------------------------------------------------------------
// LES CINQ REGLES QUE CE FICHIER TIENT
//
// 1. L'ADRESSE VIENT DE LA DONNEE, jamais d'une liste ecrite en dur. Elle est
//    lue dans `business_hub.subdomain`, editable depuis Reglages, Univers, et
//    remontee par /me/spaces sous le nom `sousDomaine`. Creer un troisieme
//    univers produit son adresse sans qu'une ligne de code change. Aucun nom de
//    domaine n'est ecrit ici : le domaine parent est deduit de l'hote courant.
//
// 2. REPLI SUR LE CHEMIN quand l'univers n'a pas d'adresse. Un univers sans
//    sous-domaine reste joignable par « /hub/<slug> ». Il ne devient JAMAIS un
//    lien mort.
//
// 3. EN DEVELOPPEMENT LOCAL, PAS DE SOUS-DOMAINE. Sur « localhost », sur une
//    adresse IP ou sur un domaine nu, on rend toujours un chemin. Ce n'est pas
//    devine : `domaineDePartage` (lib/hote-espace.ts) rend null dans ces cas,
//    parce qu'il n'y a aucun domaine parent au-dessus. Personne ne perd son
//    poste de travail.
//
// 4. MEME HOTE, ADRESSE RELATIVE. Quand la destination est servie par l'hote
//    d'ou l'on part, on rend un chemin. `router.push` reste alors une navigation
//    interne, sans rechargement complet : on ne paie un changement d'hote que
//    quand il y en a vraiment un.
//
// 5. L'ADRESSE N'ACCORDE AUCUN DROIT. Elle designe un espace, elle ne prouve
//    rien. Le middleware exige la session, et l'accueil de l'espace refuse net
//    quand /me/spaces ne liste pas cet univers (voir espace-shell.tsx). Une
//    adresse devinee n'ouvre rien du tout.
//
// ----------------------------------------------------------------------------
// CHANGER D'HOTE N'EST PAS UNE NAVIGATION INTERNE
//
// `router.push` d'un routeur Next reconnait une adresse d'un autre hote et
// bascule en navigation de document (`isExternalURL` puis `handleExternalUrl`,
// verifie dans next 15.5.7). Ca marche donc, mais s'appuyer dessus est fragile :
// `naviguerVersEspace` rend le choix EXPLICITE, et `router.prefetch` d'une
// adresse d'un autre hote est de toute facon un coup pour rien (createPrefetchURL
// rend null).
//
// Consequence a ne pas oublier : `sessionStorage` est propre a une ORIGINE. Le
// temoin de l'onde de metamorphose ne survivait donc pas a un changement d'hote.
// Il est desormais double par un cookie sur le domaine partage
// (espace-metamorphose.tsx), pour que l'espace d'arrivee joue bien son onde.
// ============================================================================

import {
  ETIQUETTE_PORTAIL,
  ETIQUETTE_VYNDRA,
  domaineDePartage,
  hoteAvecEtiquette,
} from '@/lib/hote-espace';

/**
 * Une destination d'espace, telle que /me/spaces la decrit.
 *
 * Passez l'objet `EspaceServeur` recu du serveur : ses champs portent deja ces
 * noms. Aucune transformation n'est necessaire.
 */
export interface CibleEspace {
  /** Identifiant stable : « hub-<slug> » pour un univers, « vyndra », « portail ». */
  code: string;
  /**
   * Adresse propre de l'univers (`business_hub.subdomain`), telle que le
   * dirigeant l'a fixee dans Reglages, Univers. Vide ou absente : on retombe
   * sur `href`.
   */
  sousDomaine?: string | null;
  /**
   * Chemin de repli donne par le serveur : « /hub/<slug> », « /dashboard »,
   * « /portail ». Il sert en developpement local et quand l'univers n'a pas
   * encore d'adresse propre.
   */
  href?: string | null;
}

/** Chemin servi a la racine d'un hote d'espace : le middleware y monte l'accueil. */
const RACINE = '/';

/** Chemin de repli du back-office Vyndra quand le serveur n'en donne pas. */
const REPLI_VYNDRA = '/dashboard';

/** Chemin de repli du portail salarie quand le serveur n'en donne pas. */
const REPLI_PORTAIL = '/portail';

/** Retire le port et met en minuscules, pour comparer deux hotes. */
function hoteNu(hote: string | null | undefined): string {
  return (hote ?? '').trim().toLowerCase().split(':')[0] ?? '';
}

/** L'hote qui sert la page courante, ou null hors du navigateur. */
function hoteCourantParDefaut(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.host;
}

/** Le protocole a employer pour une adresse absolue. */
function protocole(): string {
  if (typeof window === 'undefined') return 'https:';
  // Un hote d'espace n'existe qu'en HTTPS (certificat par hote, Traefik). On
  // suit quand meme le protocole courant : un poste en HTTP ne produit de toute
  // facon jamais d'adresse absolue, faute de domaine parent.
  return window.location.protocol === 'http:' ? 'http:' : 'https:';
}

/**
 * L'ADRESSE D'UN ESPACE. Appelez CETTE fonction, jamais une concatenation.
 *
 * Rend soit un chemin (« /hub/informatique », « /lancer ») quand la destination
 * est servie par l'hote courant ou qu'aucun sous-domaine n'est possible, soit
 * une adresse absolue (« https://guardinformatique.vyndra.fr/ ») quand il faut
 * changer d'hote.
 *
 * Pour naviguer, passez le resultat a `naviguerVersEspace` : un changement
 * d'hote est une navigation de document, pas une navigation interne.
 *
 * ATTENTION AU RENDU SERVEUR. Hors du navigateur, l'hote courant n'est pas
 * connu : la fonction rend alors un chemin. Ce n'est pas un probleme pour les
 * ecrans qui l'appellent aujourd'hui, tous nourris par un appel reseau donc
 * rendus apres le montage. Si vous l'appelez dans un rendu servi par le serveur
 * puis hydrate, FOURNISSEZ `hoteCourant` explicitement, sinon l'adresse
 * changerait entre les deux rendus.
 *
 * @param cible       l'espace tel que /me/spaces le decrit
 * @param hoteCourant l'hote d'ou l'on part. Par defaut celui de la page ;
 *                    a fournir explicitement cote serveur, ou il n'existe pas.
 */
export function adresseEspace(
  cible: CibleEspace,
  hoteCourant: string | null | undefined = hoteCourantParDefaut(),
): string {
  const chemin = (cible.href ?? '').trim();

  // Aucun domaine parent : developpement local, adresse IP, domaine nu. On rend
  // toujours un chemin, sinon plus personne ne peut travailler sur son poste.
  if (domaineDePartage(hoteCourant) === null) {
    return chemin || cheminDeRepli(cible);
  }

  const etiquette = etiquetteVisee(cible);
  if (etiquette === null) {
    // Univers sans adresse propre : il reste joignable par son chemin, sur
    // l'hote courant. Jamais un lien mort.
    return chemin || cheminDeRepli(cible);
  }

  const hoteVise = hoteAvecEtiquette(etiquette, hoteCourant);
  if (hoteVise === null) return chemin || cheminDeRepli(cible);

  // Meme hote : on garde une adresse relative pour que la navigation reste
  // interne. Sur son propre hote, un espace est servi a la racine.
  if (hoteVise === hoteNu(hoteCourant)) {
    return etiquette === ETIQUETTE_VYNDRA ? (chemin || REPLI_VYNDRA) : RACINE;
  }

  // L'hote change. La racine suffit : le middleware y sert l'accueil de
  // l'espace, et c'est aussi le point de depart declare par le manifeste de
  // l'application installee. Pour Vyndra, on garde le chemin demande.
  const cheminVise = etiquette === ETIQUETTE_VYNDRA ? (chemin || REPLI_VYNDRA) : RACINE;
  return `${protocole()}//${hoteVise}${cheminVise}`;
}

/**
 * L'adresse d'un ECRAN PRECIS dans un univers (ex. « /commercial/reglages/garde-fous »),
 * quand on part d'un autre hote (Vyndra).
 *
 * `adresseEspace` rend volontairement la RACINE quand l'hote change : c'est
 * l'accueil de l'univers. Un lien « Régler dans Guard Security » doit, lui, ouvrir
 * l'ecran de reglage de cet univers, pas son accueil (24/09/2026). Meme regle que
 * le lanceur (`adresseDansEspace`, app/(app)/lancer/page.tsx) : l'ecran s'ajoute a
 * l'adresse absolue de l'univers.
 *
 * Sans adresse absolue (developpement local sans domaine de partage, univers sans
 * sous-domaine), on rend le chemin seul : sur l'hote de Vyndra, le middleware
 * renvoie un ecran du commerce au lanceur, qui l'ouvre dans l'espace choisi.
 * Jamais une adresse fabriquee a la main.
 */
export function adresseEcranEspace(
  cible: CibleEspace,
  ecran: string,
  hoteCourant: string | null | undefined = hoteCourantParDefaut(),
): string {
  const racine = adresseEspace(cible, hoteCourant);
  if (!estAdresseDUnAutreHote(racine)) return ecran;
  try {
    return new URL(ecran, racine).toString();
  } catch {
    return racine;
  }
}

/** Etiquette d'hote visee par cette cible, ou null quand elle n'en a pas. */
function etiquetteVisee(cible: CibleEspace): string | null {
  if (cible.code === 'vyndra') return ETIQUETTE_VYNDRA;
  if (cible.code === 'portail') return ETIQUETTE_PORTAIL;
  const propre = (cible.sousDomaine ?? '').trim().toLowerCase();
  return propre.length > 0 ? propre : null;
}

/** Chemin de repli quand le serveur n'a pas donne de `href`. */
function cheminDeRepli(cible: CibleEspace): string {
  if (cible.code === 'vyndra') return REPLI_VYNDRA;
  if (cible.code === 'portail') return REPLI_PORTAIL;
  if (cible.code.startsWith('hub-')) return `/hub/${cible.code.slice('hub-'.length)}`;
  return RACINE;
}

/**
 * Vrai quand cette adresse designe un AUTRE hote, donc quand y aller est une
 * navigation de document.
 *
 * `adresseEspace` ne rend une adresse absolue que dans ce cas precis : le test
 * est donc exact, pas une heuristique.
 */
export function estAdresseDUnAutreHote(adresse: string): boolean {
  return /^https?:\/\//i.test(adresse.trim());
}

/**
 * Va a l'adresse d'un espace, par le bon mecanisme.
 *
 * `router.push` ne peut pas changer d'hote de facon fiable : on emploie une
 * navigation de document quand l'hote change, et le routeur sinon.
 *
 * @param adresse resultat de `adresseEspace`
 * @param pousser la navigation interne, typiquement `router.push`
 */
export function naviguerVersEspace(adresse: string, pousser: (a: string) => void): void {
  if (estAdresseDUnAutreHote(adresse)) {
    if (typeof window !== 'undefined') window.location.assign(adresse);
    return;
  }
  pousser(adresse);
}

/**
 * Prepare le chargement d'une destination, quand cela a un sens.
 *
 * Une adresse d'un autre hote n'est pas prechargeable par le routeur
 * (createPrefetchURL rend null) : on ne l'appelle pas, plutot que de faire
 * croire a une optimisation qui n'a pas lieu.
 */
export function preparerEspace(adresse: string, precharger: (a: string) => void): void {
  if (estAdresseDUnAutreHote(adresse)) return;
  precharger(adresse);
}
